import './core/interceptors';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import './App.styles.scss';
import useWindowSizeDebounced from './core/hooks/useWindowSizeDebounced';
import { HelmetProvider } from 'react-helmet-async';
import { iUser } from './core/models/Users';
import { getCurrentUserInitials, getResponsiveScale } from './core/helpers';
import useCurrentUser from './core/apiHooks/useCurrentUser';
import useUsers from './core/apiHooks/useUsers';
import Loader from './components/Loader/Loader';
import { CountriesEnum, GlobalContext } from './GlobalContext';
import { addNotify, removeNotify } from './globalNotifications';
import { AppId, cssX, Env, GtmInit, PlatformContextProvider } from '@datassential/platform-ui-lib';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Router from './core/router';
import { Slide, ToastContainer } from 'react-toastify';
import cn from 'classnames';
import useLocalStorageState from './core/hooks/useLocalStorageState';
import useGroups from './core/apiHooks/useGroups';
import RoutesEnum from './core/enums/RoutesEnum';
import Unsubscribe from './pages/Unsubscribe/Unsubscribe';

function PlatformShell() {
    const {
        currentUser: { isAdmin }
    } = useContext(GlobalContext);
    const env = useMemo(() => {
        switch ((process.env.REACT_APP_PLATFORM_ENV || '').toLowerCase()) {
            case 'qa':
                return Env.QA;
            case 'staging':
                return Env.Staging;
            default:
                return Env.Live;
        }
    }, []);

    return (
        <PlatformContextProvider
            appId={AppId.ReportPro}
            env={env}
            enableDevTools={env !== Env.Live}
            additionalUserMenu={
                isAdmin
                    ? [
                          {
                              id: 'admin',
                              content: (
                                  <a
                                      className={cn(cssX('Dropdown__item'), 'is-tall')}
                                      target={'_blank'}
                                      href={process.env.REACT_APP_ADMIN_URL ?? 'no-link'}
                                  >
                                      Tool Admin
                                  </a>
                              )
                          }
                      ]
                    : undefined
            }
        >
            {process.env.NODE_ENV !== 'development' && <GtmInit />}
            <Router />
        </PlatformContextProvider>
    );
}

function AuthenticatedApp() {
    const [selectedCountryId, setSelectedCountryId] = useLocalStorageState<CountriesEnum>('country-id', CountriesEnum.Usa);
    const [wWidth, wHeight] = useWindowSizeDebounced(1000);
    const [isSearchOpened, setIsSearchOpened] = useState(false);
    const [shouldOpenSearchOnType, setShouldOpenSearchOnType] = useState(true);
    const [isSearchDebugEnabled, setIsSearchDebugEnabled] = useState(false);
    const [users, setUsers] = useState<iUser[]>([]);
    const currentUser = useCurrentUser();
    const [currentUserInitials, setCurrentUserInitials] = useState('');
    const getCompanyUsers = useUsers();
    const { data: userGroupsData } = useGroups();
    const [inMyLibrary, setInMyLibrary] = useState(true);

    useEffect(() => {
        setUsers(getCompanyUsers.data);
    }, [getCompanyUsers.data]);

    useEffect(() => {
        if (!currentUser.data) return;
        const userInitials = getCurrentUserInitials(currentUser.data);
        setCurrentUserInitials(userInitials);

        setInMyLibrary(currentUser.data.isInMyLibraryToggledOn);
    }, [currentUser.data]);

    const getRem = (num: number) => getResponsiveScale(num, wWidth);

    function toggleIsSearchOpened() {
        setIsSearchOpened(!isSearchOpened);
    }

    useEffect(() => {
        const cb = (event: MouseEvent) => {
            if (event.altKey && event.metaKey) {
                setIsSearchDebugEnabled(!isSearchDebugEnabled);
            }
        };
        window.addEventListener('click', cb, true);

        return () => {
            window.removeEventListener('click', cb, true);
        };
    }, [isSearchDebugEnabled]);

    const contextValue = useMemo(
        () => ({
            wWidth,
            wHeight,
            isSearchOpened,
            toggleIsSearchOpened,
            setIsSearchOpened,
            shouldOpenSearchOnType,
            setShouldOpenSearchOnType,
            usersData: users,
            groupsData: userGroupsData,
            getRem,
            addNotify,
            removeNotify,
            currentUser: currentUser.data,
            currentUserInitials,
            inMyLibrary,
            setInMyLibrary,
            isSearchDebugEnabled,
            selectedCountryId,
            setSelectedCountryId
        }),
        [
            wWidth,
            wHeight,
            isSearchOpened,
            shouldOpenSearchOnType,
            users,
            userGroupsData,
            currentUser.data,
            currentUserInitials,
            inMyLibrary,
            isSearchDebugEnabled,
            selectedCountryId
        ]
    );

    return (
        <div className="App">
            <HelmetProvider>
                <GlobalContext.Provider value={contextValue}>
                    <ToastContainer
                        position="bottom-right"
                        autoClose={5000}
                        hideProgressBar
                        closeOnClick
                        pauseOnFocusLoss
                        draggable={false}
                        pauseOnHover
                        transition={Slide}
                    />

                    {currentUser.isLoading ? <Loader /> : <PlatformShell />}
                </GlobalContext.Provider>
            </HelmetProvider>
        </div>
    );
}

function App() {
    return (
        <BrowserRouter basename={`${process.env.PUBLIC_URL}/`}>
            <Routes>
                {/* Reached from email links by signed-out users, so it must render before the current-user gate. */}
                <Route
                    path={RoutesEnum.Unsubscribe}
                    element={
                        <div className="App">
                            <Unsubscribe />
                        </div>
                    }
                />
                <Route path="*" element={<AuthenticatedApp />} />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
